import React__default from 'react';
import styled, { css } from 'styled-components';
import { useQueryChildComponents, HappyWebSystemError } from '../base/SystemComponent.js';
import { ListData, ListHeader } from '../list/List.js';
import { CSSGenerateResponsive, CSSGenerateFlexAlign } from '../styles/cssGenerate.js';
import { CSSGridGenerateCompatibleTemplateColumns } from '../styles/cssGridGenerate.js';
import { normalizeResponsiveProps } from '../styles/useResponsiveProps.js';
import { Column } from './Column.js';

const ColumnsWrapper = styled.div`
  /* manually set ie11-compatible grid props */
  /* styled-components doesn't currently accept grid autoprefixer at runtime (which can provide these fixes) */
  display: -ms-grid;
  display: grid;
  ${(props)=>props.height === "fill" && css`
      grid-template-rows: 1fr;
    `}
  grid-gap: ${(props)=>props.gap}rem;
  position: relative;
  width: 100%;
  ${(props)=>CSSGenerateResponsive(props.colsCalc)(CSSGridGenerateCompatibleTemplateColumns(props.cols, props.gap))}

  & > * {
    ${(props)=>props.align !== undefined ? CSSGenerateFlexAlign(props.align) : ""};
  }
`;
const useResponsiveColumnProps = (cols, columnChildren)=>{
    // Support usage of cols / col prop values
    if (!!cols) {
        return [
            Array(cols).fill(1),
            Array(cols).fill(1),
            Array(cols).fill(1)
        ];
    }
    const calculatedCols = [
        [],
        [],
        []
    ];
    for (const column of columnChildren){
        // Prevent child `col` being set without `cols`
        const colCount = column?.props?.col;
        if (!!colCount || Array.isArray(colCount)) {
            throw new HappyWebSystemError(Columns, "col props on <Column /> components are only valid when cols are set on <Columns />");
        }
        const normalizedColumnProps = column?.props?.span ? normalizeResponsiveProps(column?.props?.span) : [
            null,
            null,
            null
        ];
        // build the responsive column calculations
        if (!!normalizedColumnProps[0] || normalizedColumnProps[0] === 0) {
            calculatedCols[0].push(normalizedColumnProps[0]);
        }
        if (!!normalizedColumnProps[1] || normalizedColumnProps[1] === 0) {
            calculatedCols[1].push(normalizedColumnProps[1]);
        }
        if (!!normalizedColumnProps[2] || normalizedColumnProps[2] === 0) {
            calculatedCols[2].push(normalizedColumnProps[2]);
        } else {
            calculatedCols[2].push(1);
        }
    }
    return calculatedCols;
};
/**
 * Layout component for creating horizontal layouts.
 * Creates a number of columns using ie11-grid compatible css-grid.
 *
 * @param gap - gap in `rem` units
 * @param padding - padding in `rem` units
 *
 * @example
 * // simple usage
 * <Columns>
 *  <Column />
 *  <Column />
 *  <Column />
 * </Columns>
 *
 * // custom usage
 * <Columns gap={1}>
 *  <Column />
 *  <Column span="content" />
 *  <Column span={4} />
 * </Columns>
 *
 */ const Columns = ({ cols , gap =0 , height ="fill" , children , ...props })=>{
    // List elements should ideally not be referenced here, however their implementation uses Columns to present their
    // layout. Ideally List elements will be refactored from using this Columns implementation internally.
    const [columns] = useQueryChildComponents(children, [
        Column,
        ListData,
        ListHeader
    ]);
    if (columns?.length === 0) {
        throw new HappyWebSystemError(Columns, "must have <Column /> components as children");
    }
    const colsCalc = useResponsiveColumnProps(cols, columns);
    return React__default.createElement(ColumnsWrapper, {
        "data-hpy-web-columns": true,
        ...props,
        ...gap ? {
            "data-hpy-web-columns-with-gap": ""
        } : {},
        colsCalc: colsCalc,
        height: height,
        cols: cols,
        gap: gap
    }, columns);
};
Columns.dataId = "data-hpy-web-columns";
Columns.displayName = "Columns";

export { Columns };
